import { useCallback, useMemo } from 'react'
import { Cell, Column, SortingRule } from 'react-table'

import { DomainLogs } from '../types'
import { AUDIT_HEADERS } from '../consts'
import { Table } from 'components/Table'
import AuditMobileTable from '../AuditMobileTable'
import type { TableHeader } from 'components/Table/types'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import AuditActionCell from './auditActionCell'
import {
  AuditTableInnerWrapper,
  AuditTableOuterWrapper,
  AuditOrangeEmail,
} from './styles'

interface AuditTableProps {
  data: DomainLogs[]
  headers: Record<AUDIT_HEADERS, TableHeader>
  defaultSortBy: SortingRule<DomainLogs>
  sortBy: SortingRule<DomainLogs>
  setSortBy: (sortBy: SortingRule<DomainLogs>) => void
  rootDomainId: string | null
}

const DateCell = (cell: Cell<DomainLogs>) => {
  return <IntlFormattedDate date={cell.row.original.created} withTime />
}

function AuditTable({
  data,
  defaultSortBy,
  headers,
  sortBy,
  setSortBy,
  rootDomainId,
}: AuditTableProps) {
  const AсtionCell = useCallback(
    (cell: Cell<DomainLogs>) => {
      return (
        <AuditActionCell
          log={cell.row.original}
          rootDomainId={rootDomainId || ''}
        />
      )
    },
    [rootDomainId]
  )

  const UserCell = useCallback((cell: Cell<DomainLogs>) => {
    const {
      user: { firstName, lastName, email },
    } = cell.row.original
    return (
      <AuditOrangeEmail
        address={email}
      >{`${lastName}, ${firstName}`}</AuditOrangeEmail>
    )
  }, [])

  const columns: readonly Column<DomainLogs>[] = useMemo(
    () => [
      {
        Header: headers.date.label,
        id: headers.date.id,
        accessor: 'created',
        Cell: DateCell,
      },
      {
        Header: headers.domain.label,
        id: headers.domain.id,
        accessor: (row) => row.domain.name,
      },
      {
        Header: headers.user.label,
        id: headers.user.id,
        accessor: (row) => row.user.email,
        Cell: UserCell,
      },
      {
        Header: headers.action.label,
        id: headers.action.id,
        accessor: 'action',
        Cell: AсtionCell,
      },
    ],
    [
      headers.action.id,
      headers.action.label,
      headers.date.id,
      headers.date.label,
      headers.domain.id,
      headers.domain.label,
      headers.user.id,
      headers.user.label,
      AсtionCell,
      UserCell,
    ]
  )

  return (
    <AuditTableOuterWrapper>
      <AuditTableInnerWrapper>
        <Table
          isPaginated={false}
          defaultSortBy={defaultSortBy}
          sortBy={sortBy}
          setSortBy={setSortBy}
          columns={columns}
          data={data}
          mobileViewComponent={AuditMobileTable}
        />
      </AuditTableInnerWrapper>
    </AuditTableOuterWrapper>
  )
}

export default AuditTable
